import Offline from 'offline/index'
import CONSTANTS from 'app/constants'
import cookies from 'js-cookie'
import DB from 'offline/db'

function clone(value) {
	return value === undefined ? undefined : JSON.parse(JSON.stringify(value))
}

function stable(value) {
	if (Array.isArray(value)) return value.map(stable)
	if (!value || typeof value !== 'object') return value

	var result = {}
	var keys = Object.keys(value).sort()
	for (var i = 0; i < keys.length; i++) result[keys[i]] = stable(value[keys[i]])
	return result
}

function sameValue(left, right) {
	return JSON.stringify(stable(left)) === JSON.stringify(stable(right))
}

function ownerPath(ownerType) {
	if (ownerType === 'users' || ownerType === 'groups' || ownerType === 'buddies') return ownerType
	throw new Error('Unsupported owner type: ' + ownerType)
}

function headers() {
	var result = { 'content-type': 'application/json' }
	var token = cookies.get('token')
	if (token) result.Authorization = token
	return result
}

function withOwnerName(params) {
	if (!params) return {}
	if (!params.name && params.slug) return Object.assign({}, params, { name: params.slug })
	return Object.assign({}, params)
}

function inferSessionUserId(params) {
	if (params && params.sessionUserId !== undefined) return params.sessionUserId
	return Number(DB.meta('sync_user_id') || 0) || null
}

function summarizePayload(payload) {
	var plays = payload && payload.plays ? payload.plays : []
	var first = plays.length ? plays[0] : null

	return {
		total: payload && payload.total,
		page: payload && payload.page,
		pages: payload && payload.pages,
		results: plays.length,
		first: first ? {
			id: first.id,
			game: first.game ? first.game.id : null,
			plays: first.plays || null,
			record: first.record || null,
			counts: first.counts || null,
			playersType: Array.isArray(first.players) ? 'array' : typeof first.players,
			playerKeys: Array.isArray(first.players)
				? first.players.length
				: Object.keys(first.players || {})
		} : null
	}
}

function findPlayRow(payload, rowId) {
	var plays = payload && payload.plays ? payload.plays : []
	for (var i = 0; i < plays.length; i++) {
		if (plays[i].id === rowId) return plays[i]
	}
	return null
}

function topLevelDiff(localRow, remoteRow, fields) {
	var diff = {}

	for (var i = 0; i < fields.length; i++) {
		var field = fields[i]
		var localValue = localRow ? localRow[field] : undefined
		var remoteValue = remoteRow ? remoteRow[field] : undefined

		if (!sameValue(localValue, remoteValue)) {
			diff[field] = {
				local: clone(localValue),
				remote: clone(remoteValue)
			}
		}
	}

	return diff
}

async function remotePost(action, params) {
	var response = await window.fetch(CONSTANTS.API + action + '.json', {
		method: 'POST',
		headers: headers(),
		credentials: 'include',
		body: JSON.stringify(params || {})
	})

	var json = await response.json()
	if (!response.ok) throw json
	return json
}

async function resolveOwner(ownerType, params) {
	var path = ownerPath(ownerType) + '/verify'
	var request = withOwnerName(params)
	var response = await remotePost(path, request)
	var owner = response && response.data && response.data.id ? response.data : response
	if (!owner || !owner.id) {
		throw new Error('Failed to resolve owner for ' + path)
	}
	return owner
}

async function ensureDbReady() {
	await DB.init()
	return DB
}

var Harness = {
	status: async function() {
		await ensureDbReady()
		return {
			router: Offline.Router.status(),
			syncUserId: inferSessionUserId({})
		}
	},

	local: async function(action, params) {
		await ensureDbReady()
		Offline.Router.setMode('local')
		// Don't inject sessionUserId by default — matches unauthenticated API calls.
		// Pass sessionUserId explicitly in params if needed.
		var request = Object.assign({}, params || {})
		if (request.sessionUserId === undefined) request.sessionUserId = null
		return Offline.Router.handle(action, request)
	},

	remote: async function(action, params) {
		return remotePost(action, Object.assign({}, params || {}, {
			sessionUserId: inferSessionUserId(params)
		}))
	},

	snapshot: async function(ownerType, params, onProgress) {
		var owner = await resolveOwner(ownerType, params)
		var context = {
			type: ownerPath(ownerType),
			id: owner.id,
			minimal: !params || params.minimal === undefined ? true : !!params.minimal
		}

		context.name = owner.name
		await Offline.Sync.snapshotSqlite(context, onProgress)

		return {
			owner: owner,
			router: Offline.Router.status()
		}
	},

	compare: async function(action, params) {
		var request = withOwnerName(clone(params) || {})
		if (request.sessionUserId === undefined) request.sessionUserId = null

		var result = await Promise.all([
			this.remote(action, request),
			this.local(action, request)
		])

		return {
			action: action,
			params: request,
			remote: result[0],
			local: result[1],
			summary: {
				remote: summarizePayload(result[0]),
				local: summarizePayload(result[1])
			}
		}
	},

	comparePlays: async function(ownerType, params) {
		var action = ownerPath(ownerType) + '/plays'
		var filter = Object.assign({}, (params && params.filter) || {})
		filter.play = Object.assign({}, filter.play || {}, {
			group: filter.play && filter.play.group !== undefined ? filter.play.group : 1
		})

		return this.compare(action, Object.assign({}, params || {}, {
			filter: filter
		}))
	},

	compareCollection: async function(ownerType, params) {
		return this.compare(ownerPath(ownerType) + '/collection', params || {})
	},

	compareRatings: async function(ownerType, params) {
		return this.compare(ownerPath(ownerType) + '/ratings', params || {})
	},

	compareLists: async function(ownerType, params) {
		return this.compare(ownerPath(ownerType) + '/lists', params || {})
	},

	compareUsers: async function(ownerType, params) {
		return this.compare(ownerPath(ownerType) + '/users', params || {})
	},

	compareAdvanced: async function(action, params, advancedTree) {
		var filter = Object.assign({}, (params && params.filter) || {})
		filter.advanced = clone(advancedTree)

		return this.compare(action, Object.assign({}, params || {}, {
			filter: filter
		}))
	},

	compareAdvancedCollection: async function(ownerType, params, advancedTree) {
		return this.compareAdvanced(ownerPath(ownerType) + '/collection', params, advancedTree)
	},

	compareAdvancedPlays: async function(ownerType, params, advancedTree) {
		var filter = Object.assign({}, (params && params.filter) || {})
		filter.play = Object.assign({}, filter.play || {}, {
			group: filter.play && filter.play.group !== undefined ? filter.play.group : 1
		})

		return this.compareAdvanced(ownerPath(ownerType) + '/plays', Object.assign({}, params || {}, {
			filter: filter
		}), advancedTree)
	},

	firstNonEmptyGroupedGroupPlay: async function(params) {
		var compared = await this.comparePlays('groups', Object.assign({}, params || {}, {
			sort: 'plays',
			sortReverse: 1,
			filter: Object.assign({}, (params && params.filter) || {}, {
				play: Object.assign({}, params && params.filter && params.filter.play ? params.filter.play : {}, { group: 1 })
			})
		}))

		var localRows = compared.local && compared.local.plays ? compared.local.plays : []
		var remoteRows = compared.remote && compared.remote.plays ? compared.remote.plays : []

		return {
			local: localRows.find(function(row) { return row.record && row.record.results > 0 }) || null,
			remote: remoteRows.find(function(row) { return row.record && row.record.results > 0 }) || null
		}
	},

	diffPlayRow: async function(ownerType, params, rowId) {
		var compared = await this.comparePlays(ownerType, params || {})
		var localRow = findPlayRow(compared.local, rowId)
		var remoteRow = findPlayRow(compared.remote, rowId)

		return {
			id: rowId,
			found: {
				local: !!localRow,
				remote: !!remoteRow
			},
			diff: topLevelDiff(localRow, remoteRow, [
				'plays',
				'record',
				'players',
				'counts',
				'user',
				'users',
				'group',
				'game'
			]),
			local: localRow,
			remote: remoteRow
		}
	},

	benchmark: async function(scenarios, iterations) {
		iterations = iterations || 3
		await ensureDbReady()
		Offline.Router.setMode('local')

		var results = []

		for (var si = 0; si < scenarios.length; si++) {
			var scenario = scenarios[si]
			var timings = []
			var profiles = []
			var lastResult = null

			for (var i = 0; i < iterations; i++) {
				DB.startProfiling()
				var start = performance.now()

				try {
					lastResult = await Offline.Router.handle(
						scenario.action,
						Object.assign({}, scenario.params || {}, {
							sessionUserId: inferSessionUserId(scenario.params)
						})
					)
				} catch (e) {
					lastResult = { error: e.message || String(e) }
				}

				var elapsed = performance.now() - start
				var profile = DB.stopProfiling()
				timings.push(Math.round(elapsed * 100) / 100)
				profiles.push(profile)
			}

			timings.sort(function(a, b) { return a - b })
			var sum = 0
			for (var t = 0; t < timings.length; t++) sum += timings[t]

			// Summarize phases from the last profile run
			var phaseSummary = {}
			var querySummary = { count: 0, totalMs: 0, slowest: null }
			var lastProfile = profiles[profiles.length - 1] || []
			for (var p = 0; p < lastProfile.length; p++) {
				var entry = lastProfile[p]
				if (entry.type === 'phase') {
					phaseSummary[entry.label] = entry.ms
				} else if (entry.type === 'query' || entry.type === 'scalar') {
					querySummary.count++
					querySummary.totalMs += entry.ms
					if (!querySummary.slowest || entry.ms > querySummary.slowest.ms) {
						querySummary.slowest = { sql: entry.sql, ms: entry.ms, rows: entry.rows }
					}
				}
			}

			results.push({
				name: scenario.name || scenario.action,
				iterations: iterations,
				timings: timings,
				min: timings[0],
				max: timings[timings.length - 1],
				avg: Math.round((sum / timings.length) * 100) / 100,
				total: lastResult && lastResult.total,
				phases: phaseSummary,
				queries: querySummary,
				profile: lastProfile
			})
		}

		return results
	},

	diffFirstNonEmptyGroupedGroupPlay: async function(params) {
		var rows = await this.firstNonEmptyGroupedGroupPlay(params)
		var rowId = rows.local ? rows.local.id : (rows.remote ? rows.remote.id : null)
		if (!rowId) return { id: null, diff: {}, local: null, remote: null }
		return this.diffPlayRow('groups', Object.assign({}, params || {}, {
			sort: 'plays',
			sortReverse: 1,
			filter: Object.assign({}, (params && params.filter) || {}, {
				play: Object.assign({}, params && params.filter && params.filter.play ? params.filter.play : {}, { group: 1 })
			})
		}), rowId)
	}
}

if (typeof window !== 'undefined') {
	window.OfflineHarness = Harness
}

export default Harness
